import React, { useState, useEffect, useRef } from 'react';

// The whole verse is one chunk, so ←/→ and tapping speak the full verse
function splitIntoChunks(verseText) {
  return verseText ? [verseText] : [];
}

// Mirrors the modal's TTS toggle so speakText can stay a plain module function
let ttsEnabled = true;
let ttsRate = 1;
const TTS_RATES = [0.5, 0.75, 1];

const SPECTRUM_STOPS = ['#d32f2f 0%', '#e07b2e 14%', '#f5e050 26%', '#fdff5a 33%', '#8fe03a 42%', '#4cb85a 50%', '#6ff2ff 62%', '#3f8fe0 74%', '#0a0a8c 82%', '#5a1fc4 92%', '#6a2fb0 100%'];
// Mirrored copy appended so the band tiles seamlessly while it slides
const SPECTRUM_LOOP = `linear-gradient(to right, ${SPECTRUM_STOPS.join(', ')}, ${[...SPECTRUM_STOPS].reverse().map((st, i) => { const [c, pct] = st.split(' '); return `${c} ${100 + (100 - parseFloat(pct))}%`; }).join(', ')})`;

function speakText(text, onEnd) {
  if (!ttsEnabled || !text || !('speechSynthesis' in window)) return;
  window.speechSynthesis.cancel();
  const utt = new SpeechSynthesisUtterance(text);
  utt.lang = 'en-US';
  utt.rate = ttsRate;
  const voices = window.speechSynthesis.getVoices();
  const voice =
    voices.find(v => v.lang.startsWith('en') && (v.name.includes('Enhanced') || v.name.includes('Premium'))) ||
    voices.find(v => v.lang.startsWith('en-US')) ||
    voices.find(v => v.lang.startsWith('en'));
  if (voice) utt.voice = voice;
  if (onEnd) utt.onend = onEnd;
  window.speechSynthesis.speak(utt);
}

function verseToString(v) {
  if (!v) return '';
  return typeof v === 'string' ? v : (v.text || v.verse || String(v));
}

export default function VerseMemorizeModal({
  open, onClose,
  verseLabel, verseText,
  bookName, chapter, startVerseNumber,
  chapterVerses,
  isDarkMode, isSepiaMode,
}) {
  // null = show verse picker; number = 0-based index of selected verse
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [activeChunk, setActiveChunk] = useState(null);
  // Verse that swipe-up returns to (0-based); defaults to verse 1
  const [homeIdx, setHomeIdx] = useState(0);
  // Bumped on every manual speak action to invalidate any in-flight '/' auto-play chain
  const playTokenRef = useRef(0);
  const [ttsOn, setTtsOn] = useState(() => {
    try { return localStorage.getItem('memorize-tts') !== 'off'; } catch { return true; }
  });
  ttsEnabled = ttsOn;
  const [rate, setRate] = useState(() => {
    try {
      const v = parseFloat(localStorage.getItem('memorize-tts-rate'));
      return TTS_RATES.includes(v) ? v : 1;
    } catch { return 1; }
  });
  ttsRate = rate;
  // Cycles 0.5x -> 0.75x -> 1x -> back to 0.5x
  const cycleRate = () => {
    const next = TTS_RATES[(TTS_RATES.indexOf(rate) + 1) % TTS_RATES.length];
    ttsRate = next;
    setRate(next);
    try { localStorage.setItem('memorize-tts-rate', String(next)); } catch { /* ignore */ }
  };
  // Body text scale (rem multiplier), adjusted by the A-/A+ buttons
  const [fontScale, setFontScale] = useState(() => {
    try {
      const v = parseFloat(localStorage.getItem('memorize-font-scale'));
      return v >= 0.8 && v <= 2.2 ? v : 1;
    } catch { return 1; }
  });
  const [spectrumRunning, setSpectrumRunning] = useState(() => {
    try { return localStorage.getItem('memorize-spectrum-running') === 'on'; } catch { return false; }
  });
  const toggleSpectrum = () => {
    const next = !spectrumRunning;
    setSpectrumRunning(next);
    try { localStorage.setItem('memorize-spectrum-running', next ? 'on' : 'off'); } catch { /* ignore */ }
  };
  const changeFont = (delta) => {
    const next = Math.round(Math.min(2.2, Math.max(0.8, fontScale + delta)) * 100) / 100;
    setFontScale(next);
    try { localStorage.setItem('memorize-font-scale', String(next)); } catch { /* ignore */ }
  };
  const toggleTts = () => {
    const next = !ttsOn;
    ttsEnabled = next;
    if (!next) { playTokenRef.current++; window.speechSynthesis?.cancel(); }
    setTtsOn(next);
    try { localStorage.setItem('memorize-tts', next ? 'on' : 'off'); } catch { /* ignore */ }
  };

  const bg        = isDarkMode ? '#1e2235' : isSepiaMode ? '#f5efe0' : '#ffffff';
  const textColor = isDarkMode ? '#e0e0e0' : isSepiaMode ? '#5a4a2a' : '#1a1a1a';
  const border    = isDarkMode ? '#3a3f5c' : isSepiaMode ? '#c9b99a' : '#e5e7eb';
  const verseBg   = isDarkMode ? '#252840' : isSepiaMode ? '#ede0c8' : '#f9fafb';
  const closeBg   = isDarkMode ? '#444'    : isSepiaMode ? '#d4c9a8' : '#e5e7eb';
  const subText   = isDarkMode ? '#9ca3af' : isSepiaMode ? '#8a7a5a' : '#6b7280';
  const accent    = isDarkMode ? '#60a5fa' : '#2563eb';
  const hoverBg   = isDarkMode ? '#2d3148' : isSepiaMode ? '#e8ddc8' : '#f3f4f6';
  const activeBg  = isDarkMode ? '#1e3a5f' : '#dbeafe';

  const hasChapterVerses = chapterVerses && chapterVerses.length > 0;

  // Resolve the active verse text and label
  const activeVerseText = selectedIdx !== null && hasChapterVerses
    ? verseToString(chapterVerses[selectedIdx])
    : verseText;
  const activeVerseLabel = selectedIdx !== null && hasChapterVerses && bookName && chapter
    ? `${bookName} ${chapter}:${selectedIdx + 1}`
    : verseLabel;

  const chunks = splitIntoChunks(activeVerseText);
  // Header shows just chapter:verse (the book name gets truncated on mobile)
  const activeVerseNumber = selectedIdx !== null && hasChapterVerses ? selectedIdx + 1 : startVerseNumber;

  // Move to a different verse, resetting the active chunk back to 1 (like pressing '1')
  const jumpToVerse = (newIdx, speak = true) => {
    if (selectedIdx === null || newIdx === selectedIdx) return;
    playTokenRef.current++; // cancel any in-progress '/' auto-play
    window.speechSynthesis?.cancel(); // a silent jump shouldn't leave the old verse talking
    setSelectedIdx(newIdx);
    const newChunks = splitIntoChunks(verseToString(chapterVerses[newIdx]));
    setActiveChunk(0);
    if (speak) speakText(newChunks[0]);
  };
  const goToPrev = (speak = true) => { if (selectedIdx !== null) jumpToVerse(Math.max(0, selectedIdx - 1), speak); };
  const goToNext = (speak = true) => { if (selectedIdx !== null) jumpToVerse(Math.min((chapterVerses?.length ?? 1) - 1, selectedIdx + 1), speak); };

  // Shared by swipes and arrow keys: up = silent jump to the home verse, down = read the verse aloud
  const readCurrent = () => {
    playTokenRef.current++;
    setActiveChunk(0);
    speakText(chunks[0]);
  };
  const goHome = () => {
    if (selectedIdx === homeIdx) readCurrent(); // already home: repeat instead of a dead key
    else jumpToVerse(homeIdx, false);
  };

  // '/' continues speaking chunks one after another starting from the highlighted
  // one, advancing the highlight as each finishes — like pressing Enter to keep going.
  const speakFromHighlight = () => {
    const token = ++playTokenRef.current;
    const startIdx = activeChunk === null ? 0 : activeChunk;
    const playFrom = (idx) => {
      if (idx >= chunks.length) return;
      setActiveChunk(idx);
      speakText(chunks[idx], () => {
        if (playTokenRef.current !== token) return; // interrupted by a manual action
        playFrom(idx + 1);
      });
    };
    playFrom(startIdx);
  };

  // Reset when modal opens
  useEffect(() => {
    if (open) {
      playTokenRef.current++; // cancel any in-progress '/' auto-play from a prior open
      // If chapterVerses, start at picker; else go straight to verse view
      if (hasChapterVerses) {
        const idx = startVerseNumber ? startVerseNumber - 1 : null;
        setSelectedIdx(idx !== null ? Math.max(0, idx) : null);
      } else {
        setSelectedIdx(null);
      }
      setActiveChunk(null);
      setHomeIdx(0);
    }
  }, [open, startVerseNumber, hasChapterVerses]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        playTokenRef.current++;
        window.speechSynthesis?.cancel();
        if (selectedIdx !== null && hasChapterVerses) { setSelectedIdx(null); } else { onClose(); }
        return;
      }
      if (selectedIdx !== null) {
        if (e.key === 'ArrowLeft') { e.preventDefault(); goToPrev(false); return; }
        if (e.key === 'ArrowRight') { e.preventDefault(); goToNext(false); return; }
        if (e.key === 'ArrowUp') { e.preventDefault(); goHome(); return; }
        if (e.key === 'ArrowDown') { e.preventDefault(); readCurrent(); return; }
        if (e.key === '/') {
          e.preventDefault();
          speakFromHighlight();
          return;
        }
        if (/^[1-9]$/.test(e.key)) {
          const idx = parseInt(e.key) - 1;
          if (chunks[idx] !== undefined) {
            e.preventDefault();
            playTokenRef.current++;
            setActiveChunk(idx);
            speakText(chunks[idx]);
          }
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedIdx, homeIdx, hasChapterVerses, chunks, activeChunk, onClose]);

  // Stop the browser's pull-to-refresh from reloading the page on a swipe down
  const bodyRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const html = document.documentElement;
    const prevHtml = html.style.overscrollBehaviorY;
    const prevBody = document.body.style.overscrollBehaviorY;
    html.style.overscrollBehaviorY = 'none';
    document.body.style.overscrollBehaviorY = 'none';
    // Older iOS ignores overscroll-behavior, so also cancel vertical drags when the body can't scroll
    const el = bodyRef.current;
    let startY = 0;
    const onStart = (e) => { startY = e.touches[0].clientY; };
    const onMove = (e) => {
      if (!el) return;
      const dy = e.touches[0].clientY - startY;
      const scrollable = el.scrollHeight > el.clientHeight + 1;
      if (!scrollable || (dy > 0 && el.scrollTop <= 0)) e.preventDefault();
    };
    el?.addEventListener('touchstart', onStart, { passive: true });
    el?.addEventListener('touchmove', onMove, { passive: false });
    return () => {
      html.style.overscrollBehaviorY = prevHtml;
      document.body.style.overscrollBehaviorY = prevBody;
      el?.removeEventListener('touchstart', onStart);
      el?.removeEventListener('touchmove', onMove);
    };
  }, [open, selectedIdx]);

  // Touch swipes mirror the arrow keys: left/right = next/previous verse and up = back to the home verse (both silent), down = read the verse aloud
  const touchStartRef = useRef(null);
  const onTouchStart = (e) => {
    const t = e.touches[0];
    touchStartRef.current = { x: t.clientX, y: t.clientY };
  };
  const onTouchEnd = (e) => {
    const start = touchStartRef.current;
    touchStartRef.current = null;
    if (!start || selectedIdx === null) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    const SWIPE_MIN = 50;
    if (Math.max(Math.abs(dx), Math.abs(dy)) < SWIPE_MIN) return;
    if (Math.abs(dx) > Math.abs(dy)) {
      if (dx < 0) goToNext(false); else goToPrev(false); // swipe left = next verse (silent)
    } else {
      // Skip vertical swipes when the body is long enough to scroll instead
      const body = e.currentTarget;
      if (body.scrollHeight > body.clientHeight + 1) return;
      if (dy < 0) goHome(); else readCurrent();
    }
  };

  if (!open) return null;

  const showPicker = selectedIdx === null && hasChapterVerses;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 10100,
        display: 'flex', alignItems: 'stretch', justifyContent: 'stretch',
        background: bg, padding: 0,
      }}
      role="dialog"
      aria-modal="true"
      aria-label={activeVerseLabel}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: bg, color: textColor,
          width: '100%', height: '100%',
          display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '12px 18px', borderBottom: `1px solid ${border}`, flexShrink: 0, gap: 8,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            {selectedIdx !== null && hasChapterVerses && (
              <button
                onClick={() => goToPrev(false)}
                disabled={selectedIdx <= 0}
                style={{
                  padding: '3px 8px', fontSize: '0.82rem', fontWeight: 700,
                  border: `1px solid ${border}`, borderRadius: 6,
                  background: hoverBg, color: selectedIdx <= 0 ? subText : textColor,
                  cursor: selectedIdx <= 0 ? 'default' : 'pointer', flexShrink: 0,
                  opacity: selectedIdx <= 0 ? 0.4 : 1,
                }}
                aria-label="Previous verse"
              >←</button>
            )}
            <h2 style={{
              margin: 0, fontSize: '0.98rem', fontWeight: 700,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {showPicker ? (bookName && chapter ? `${bookName} ${chapter}` : (verseLabel || 'Verses')) : (activeVerseNumber && chapter ? `${chapter}:${activeVerseNumber}` : activeVerseLabel)}
            </h2>
            {selectedIdx !== null && hasChapterVerses && (
              <button
                onClick={() => goToNext(false)}
                disabled={selectedIdx >= (chapterVerses?.length ?? 1) - 1}
                style={{
                  padding: '3px 8px', fontSize: '0.82rem', fontWeight: 700,
                  border: `1px solid ${border}`, borderRadius: 6,
                  background: hoverBg,
                  color: selectedIdx >= (chapterVerses?.length ?? 1) - 1 ? subText : textColor,
                  cursor: selectedIdx >= (chapterVerses?.length ?? 1) - 1 ? 'default' : 'pointer', flexShrink: 0,
                  opacity: selectedIdx >= (chapterVerses?.length ?? 1) - 1 ? 0.4 : 1,
                }}
                aria-label="Next verse"
              >→</button>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          {[['A-', -0.1, 'Smaller text', fontScale <= 0.8], ['A+', 0.1, 'Larger text', fontScale >= 2.2]].map(([label, delta, title, disabled]) => (
            <button
              key={label}
              onClick={() => changeFont(delta)}
              disabled={disabled}
              title={title}
              aria-label={title}
              style={{
                padding: '3px 8px', fontSize: '0.82rem', fontWeight: 700,
                border: `1px solid ${border}`, borderRadius: 6,
                background: hoverBg, color: disabled ? subText : textColor,
                cursor: disabled ? 'default' : 'pointer', flexShrink: 0,
                opacity: disabled ? 0.4 : 1,
              }}
            >{label}</button>
          ))}
          <button
            onClick={toggleSpectrum}
            title={spectrumRunning ? 'Spectrum running (click to stop)' : 'Spectrum still (click to run)'}
            aria-pressed={spectrumRunning}
            aria-label="Toggle running spectrum"
            style={{
              padding: '3px 8px', fontSize: '0.82rem', fontWeight: 700,
              border: `1px solid ${border}`, borderRadius: 6,
              background: hoverBg, color: spectrumRunning ? accent : subText,
              cursor: 'pointer', flexShrink: 0,
            }}
          >🌈</button>
          <button
            onClick={cycleRate}
            title={`Speech speed ${rate}x (click to change)`}
            aria-label={`Speech speed ${rate}x`}
            style={{
              padding: '3px 8px', fontSize: '0.82rem', fontWeight: 700,
              border: `1px solid ${border}`, borderRadius: 6,
              background: hoverBg, color: accent,
              cursor: 'pointer', flexShrink: 0, minWidth: 44,
            }}
          >{rate}x</button>
          <button
            onClick={toggleTts}
            title={ttsOn ? 'Speech on (click to mute)' : 'Speech off (click to unmute)'}
            aria-pressed={ttsOn}
            style={{
              padding: '3px 8px', fontSize: '0.82rem', fontWeight: 700,
              border: `1px solid ${border}`, borderRadius: 6,
              background: hoverBg, color: ttsOn ? accent : subText,
              cursor: 'pointer', flexShrink: 0,
            }}
          >{ttsOn ? '🔊' : '🔇'}</button>
          {selectedIdx !== null ? (
            <button
              onClick={() => { playTokenRef.current++; speakText(chunks.join(' ')); }}
              style={{
                padding: '3px 10px', fontSize: '0.82rem', fontWeight: 700,
                border: `1px solid ${border}`, borderRadius: 6,
                background: hoverBg, color: accent,
                cursor: 'pointer', flexShrink: 0,
              }}
              aria-label="Speak all chunks"
            >▶ All</button>
          ) : null}
          <button
            onClick={() => { playTokenRef.current++; window.speechSynthesis?.cancel(); onClose(); }}
            style={{
              width: 32, height: 32, border: 'none', borderRadius: 6,
              cursor: 'pointer', background: closeBg, color: textColor,
              fontWeight: 700, fontSize: 16, flexShrink: 0,
            }}
            aria-label="Close"
          >✕</button>
          </div>
        </div>

        {/* Body */}
        <div
          ref={bodyRef}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          style={{ overflowY: 'auto', overscrollBehavior: 'none', flex: 1, padding: '12px 18px' }}
        >
          {showPicker ? (
            /* ── Verse picker ── */
            <>
              <p style={{
                margin: '0 0 10px', fontSize: '0.72rem', color: subText,
                textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600,
              }}>
                Choose a verse to memorize
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {chapterVerses.map((v, i) => {
                  const text = verseToString(v);
                  return (
                    <button
                      key={i}
                      onClick={() => { setSelectedIdx(i); setActiveChunk(null); }}
                      style={{
                        display: 'flex', alignItems: 'flex-start', gap: 12,
                        padding: '10px 14px', borderRadius: 8, cursor: 'pointer',
                        border: `1px solid ${border}`,
                        background: verseBg, color: textColor,
                        textAlign: 'left', width: '100%',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = hoverBg; }}
                      onMouseLeave={e => { e.currentTarget.style.background = verseBg; }}
                    >
                      <span style={{
                        fontWeight: 700, color: accent, fontSize: '0.82rem',
                        minWidth: 22, flexShrink: 0, paddingTop: 2,
                      }}>
                        {i + 1}
                      </span>
                      <span style={{ lineHeight: 1.5, fontSize: `${0.88 * fontScale}rem`, textAlign: 'left', color: subText }}>
                        {text.slice(0, 70)}{text.length > 70 ? '…' : ''}
                      </span>
                    </button>
                  );
                })}
              </div>
            </>
          ) : (
            /* ── Chunks view ── */
            <>
              {hasChapterVerses && (
                <label style={{
                  display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10,
                  fontSize: '0.82rem', fontWeight: 600, color: subText,
                }}>
                  Swipe up returns to
                  <select
                    value={homeIdx}
                    onChange={e => setHomeIdx(Number(e.target.value))}
                    style={{
                      padding: '4px 8px', fontSize: '0.9rem', fontWeight: 700,
                      border: `1px solid ${border}`, borderRadius: 6,
                      background: hoverBg, color: textColor,
                    }}
                  >
                    {chapterVerses.map((_, i) => (
                      <option key={i} value={i}>{chapter ? `${chapter}:${i + 1}` : `Verse ${i + 1}`}</option>
                    ))}
                  </select>
                </label>
              )}
              {/* Color spectrum band */}
              <style>{`
                @keyframes memorize-spectrum-slide { from { background-position: 0% 0; } to { background-position: 200% 0; } }
                @media (prefers-reduced-motion: reduce) { .memorize-spectrum { animation: none !important; } }
              `}</style>
              <div
                role="button"
                tabIndex={0}
                aria-pressed={spectrumRunning}
                aria-label={spectrumRunning ? 'Stop spectrum' : 'Start spectrum'}
                title={spectrumRunning ? 'Tap to stop' : 'Tap to start'}
                onClick={toggleSpectrum}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); toggleSpectrum(); } }}
                className="memorize-spectrum"
                style={{
                  height: 48, borderRadius: 8, marginBottom: 12, cursor: 'pointer',
                  background: SPECTRUM_LOOP,
                  backgroundSize: '200% 100%',
                  backgroundRepeat: 'repeat-x',
                  // Paused (not removed) so stopping freezes the colours in place
                  animation: 'memorize-spectrum-slide 5s linear infinite',
                  animationPlayState: spectrumRunning ? 'running' : 'paused',
                }}
              />
              {chunks.slice(0, 9).map((chunk, i) => (
                <div
                  key={i}
                  onClick={() => { playTokenRef.current++; setActiveChunk(i); speakText(chunk); }}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: 10,
                    padding: '9px 12px', borderRadius: 7, cursor: 'pointer',
                    marginBottom: 4,
                    background: activeChunk === i ? activeBg : 'transparent',
                    border: activeChunk === i ? `1px solid ${accent}40` : '1px solid transparent',
                  }}
                  onMouseEnter={e => { if (activeChunk !== i) e.currentTarget.style.background = hoverBg; }}
                  onMouseLeave={e => { e.currentTarget.style.background = activeChunk === i ? activeBg : 'transparent'; }}
                >
                  <span style={{
                    fontWeight: 700, color: accent, minWidth: 18,
                    fontSize: `${0.92 * fontScale}rem`, lineHeight: 1.7, flexShrink: 0,
                  }}>
                    {activeVerseNumber || i + 1}
                  </span>
                  <span style={{ lineHeight: 1.65, fontSize: `${0.97 * fontScale}rem` }}>{chunk}</span>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
